/**
 * Hook for fetching and caching the Playing Wallet address (v2).
 *
 * Uses Privy JWT authentication — the backend identifies the user from the token,
 * not from an address in the URL. The `privyAddress` parameter is only used as
 * an enable guard (non-null means "this is a Privy wallet, fetch its Playing Wallet")
 * and as a queryKey scope to prevent cross-user cache collisions.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchPlayingWallet } from '@/lib/playing-wallet-api';
import { useAuth } from '@/hooks/useAuth';

export function usePlayingWallet(privyAddress: string | null) {
  const { getAccessToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['playing-wallet', privyAddress],
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) return null;
      return fetchPlayingWallet(token);
    },
    enabled: !!privyAddress && isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 min — conservative; address rarely changes
  });
}
